import React, { useContext, useCallback, useState, useEffect, useMemo, useLayoutEffect } from 'react';
import { makeStyles, Theme, Checkbox, FormControlLabel, Button, Paper } from '@material-ui/core';
import clsx from 'clsx';
import { ritzDataContext, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { capitalizeFirst, useModeFormStyle, GenericMessageBox, useModePanelStyle, SlideOutComp, SearchBox, capitalizeAll }
    from '@moderepo/mode-app-components';
import { hideSlideOutPanel, modeUIContext, setIsEditingForm } from '@moderepo/mode-ui-state';
import { useRitzTranslation } from '../../utils/customHooks';
import { ChartMetricInfo, getLongestMetricNameInPixel } from '.';



const useStyle = makeStyles((theme: Theme) => {
    return {
        root: {
            maxHeight: '100%',
        },

        panelContent: {
            maxHeight    : '100%',
            display      : 'flex',
            flexDirection: 'column',
            overflow     : 'hidden',
        },

        selectMetricsSection: {
            display      : 'flex',
            flexDirection: 'column',
            overflow     : 'hidden',
        },

        message: {
        },

        metricsContainerWrapper: {
            overflow: 'auto',
        },

        metricsContainer: {
            paddingLeft: theme.spacing(2),
        },

        metricsDivider: {
            width: '100%',
        },

        metricsGroup: {
            display       : 'flex',
            flexDirection : 'row',
            alignItems    : 'center',
            justifyContent: 'flex-start',
            flexWrap      : 'wrap',
            alignSelf     : 'center',
        },

        metric: {
            whiteSpace : 'normal',
            wordBreak  : 'break-word',
            marginRight: 0,
        },

        metricLabel: {
            display       : 'flex',
            alignItems    : 'center',
            justifyContent: 'flex-start',
            '& .label'    : {
                color     : theme.palette.text.primary,
                lineHeight: '1.2em',
            },
            '&:before': {
                content    : '""',
                display    : 'inline-block',
                minWidth   : 15,
                height     : 15,
                background : 'currentColor',
                marginRight: 5,
                border     : `1px solid ${theme.palette.text.primary}`,
            },
        },

        metricsActions: {
            display       : 'flex',
            flexDirection : 'row',
            alignItems    : 'center',
            justifyContent: 'flex-start',
            marginTop     : theme.spacing(1),
            marginBottom  : theme.spacing(1),
        },

        searchBox: {
            flex: 1,
        },

        selectAllButton: {
            width     : 'fit-content',
            marginLeft: theme.spacing(2),
        },
    };
});



export interface MultiDataSourcesViewOptionsSlideOutContainerProps {
    readonly projectId: number;
    readonly metrics: {
        readonly numericMetrics: readonly ChartMetricInfo[];
        readonly tagMetrics: readonly ChartMetricInfo[];
    }
    readonly selectedMetricIdsMap: {
        readonly numericMetrics: {
            readonly [metricId: string]: boolean | undefined;
        };
        readonly tagMetrics: {
            readonly [metricId: string]: boolean | undefined;
        };
    };

    readonly allowUnselectNumericMetrics?: boolean | undefined;
    readonly allowUnselectTagMetrics?: boolean | undefined;

    // Handler to handle the event when the user finish selecting the metrics and click "Done". Return true if successful and false otherwise
    readonly onViewOptionsChanged: (
        options: {
            readonly newSelectedMetricIdsMap: {
                readonly numericMetrics: {
                    readonly [metricId: string]: boolean | undefined;
                };
                readonly tagMetrics: {
                    readonly [metricId: string]: boolean | undefined;
                };
            } | undefined;
        }
    )=> void;
}


/**
 * This container is used for displaying the options for exporting data for some numeric/tag metrics. This component shows a list of metrics
 * the user select/unselect, the data format to export in, and a file name for the export file.
 */
export const MultiDataSourcesViewOptionsSlideOutContainer = (props: MultiDataSourcesViewOptionsSlideOutContainerProps) => {
    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const { state:dataState } = useContext(ritzDataContext);
    const { projectId } = props;
    const project = selectProjectById(dataState, projectId);
    const { trans } = useRitzTranslation(project);
    const [metricSearchText, setMetricSearchText] = useState<string>('');
    const classes = useStyle();
    const formClasses = useModeFormStyle();
    const panelClasses = useModePanelStyle();



    const filteredNumericMetrics = useMemo(() => {
        const searchText = metricSearchText.trim().toLowerCase();

        if (searchText.length > 0) {
            // If there is some search text then filter out the numeric metrics by the searchText
            return props.metrics.numericMetrics.filter((metric) => {
                return metric.displayName.toLowerCase().includes(searchText) || metric.name.toLowerCase().includes(searchText);
            });
        }

        // Else return all numericMetrics
        return props.metrics.numericMetrics;
    }, [metricSearchText, props.metrics.numericMetrics]);


    const filteredTagMetrics = useMemo(() => {
        const searchText = metricSearchText.trim().toLowerCase();

        if (searchText.length > 0) {
            // If there is some search text then filter out the tag metrics by the searchText
            return props.metrics.tagMetrics.filter((metric) => {
                return metric.displayName.toLowerCase().includes(searchText) || metric.name.toLowerCase().includes(searchText);
            });
        }

        // Else return all tagMetrics
        return props.metrics.tagMetrics;
    }, [metricSearchText, props.metrics.tagMetrics]);



    // A map of selected metrics which the key is the metric ID and the value is true/false to indicate if the metric is selected
    // We will initialize this map with the provided props.selectedMetricIdsMap
    const [selectedMetricIdsMap, setSelectedMetricsMap] = useState<{
        readonly numericMetrics: {
            readonly [metricId: string]: boolean | undefined;
        };
        readonly tagMetrics: {
            readonly [metricId: string]: boolean | undefined;
        }
    }>(props.selectedMetricIdsMap);
    const [isMetricsSelectionModified, setIsMetricsSelectionModified] = useState<boolean>(false);


    /**
     * Turn on/off edit mode LOCALLY and GLOBALLY
     * @param value - The edit mode true/false
     */
    const onChangeEditingMode = useCallback((value: boolean) => {
        // turn on/off editing mode GLOBALLY so that the system can disable all other components
        uiDispatch(setIsEditingForm(value));
    }, [uiDispatch]);


    /**
     * Automatically set is in editing mode to true since this component is used for selecting metrics to be exported therefore it will always
     * be in editing mode.
     */
    useEffect(() => {
        onChangeEditingMode(true);
    }, [onChangeEditingMode]);


    /**
     * Unselected the current selected Alert entity
     */
    const hidePreviewPanel = useCallback(() => {
        uiDispatch(hideSlideOutPanel());
        onChangeEditingMode(false);
    }, [onChangeEditingMode, uiDispatch]);


    /**
     * Toggle one of the metric's selection state.
     */
    const onToggleOneMetric = useCallback((metricId: string, isNumeric: boolean, value: boolean) => {

        // Stop if the user is not allowed to unselect the metrics
        if (isNumeric && props.allowUnselectNumericMetrics === false) {
            return;
        }
        if (!isNumeric && props.allowUnselectTagMetrics === false) {
            return;
        }


        setSelectedMetricsMap((currentValue) => {
            // Copy the current selectedMetricsMap.numericMetrics to keep the previous state
            const newSelectedNumericMetricIdsMap = {
                ...currentValue.numericMetrics,
            };

            if (isNumeric) {
                // update the state of only the specified metricId
                newSelectedNumericMetricIdsMap[metricId] = value;
            }

            // Copy the current selectedMetricsMap.tagMetrics to keep the previous state
            const newSelectedTagMetricIdsMap = {
                ...currentValue.tagMetrics,
            };

            if (!isNumeric) {
                // update the state of only the specified metricId
                newSelectedTagMetricIdsMap[metricId] = value;
            }

            return {
                numericMetrics: newSelectedNumericMetricIdsMap,
                tagMetrics    : newSelectedTagMetricIdsMap,
            };
        });

        setIsMetricsSelectionModified(true);
    }, [props.allowUnselectNumericMetrics, props.allowUnselectTagMetrics]);



    /**
     * On selectAll clicked. We will select/unselect all FILTERED metrics that are currently shown
     */
    const toggleAllMetrics = useCallback((value: boolean) => {

        setSelectedMetricsMap((currentValue) => {
            // Copy the current selectedMetricsMap.numericMetrics to keep the previous state
            const newSelectedNumericMetricIdsMap = {
                ...currentValue.numericMetrics,
            };

            // Now update the idsMap for only the metrics currently in the filtered list of metrics
            if (props.allowUnselectNumericMetrics !== false) {
                filteredNumericMetrics.forEach((metric) => {
                    newSelectedNumericMetricIdsMap[metric.id] = value;
                });
            }

            // Copy the current selectedMetricsMap.tagMetrics to keep the previous state
            const newSelectedTagMetricIdsMap = {
                ...currentValue.tagMetrics,
            };

            // Now update the idsMap for only the metrics currently in the filtered list of metrics
            if (props.allowUnselectTagMetrics !== false) {
                filteredTagMetrics.forEach((metric) => {
                    newSelectedTagMetricIdsMap[metric.id] = value;
                });
            }

            return {
                numericMetrics: newSelectedNumericMetricIdsMap,
                tagMetrics    : newSelectedTagMetricIdsMap,
            };
        });
        setIsMetricsSelectionModified(true);

    }, [filteredNumericMetrics, filteredTagMetrics, props.allowUnselectNumericMetrics, props.allowUnselectTagMetrics]);



    /**
     * On Done updating the view settings, call the callback function and pass it the new settings
     */
    const onDoneUpdatingSettings = useCallback(async (event: React.FormEvent) => {
        event.stopPropagation();
        event.preventDefault();


        props.onViewOptionsChanged({
            newSelectedMetricIdsMap: isMetricsSelectionModified
                ? JSON.parse(JSON.stringify(selectedMetricIdsMap))  // Return a clone instead of a reference to the map
                : undefined,
        });

        hidePreviewPanel();

    }, [hidePreviewPanel, isMetricsSelectionModified, props, selectedMetricIdsMap]);



    // Find the longest metric name. We will use this value to set the width of the metric label
    const [metricLabelWidthInPixel, setMetricLabelWidthInPixel] = useState<number>(0);

    useLayoutEffect(() => {
        // Get all the labels displayed
        const metricsLabels = document.getElementsByClassName(classes.metricLabel);
        if (metricsLabels.length > 0) {
            // Use one of the labels and get the font settings for that label. To calculate the width of the metric correctly, we will
            // need to get the font settings
            const metricLabel = metricsLabels[0];
            const style = window.getComputedStyle(metricLabel);
            const fontStyle = `${style['font-weight']} ${style['font-size']} ${style['font-family']}`;

            // Get the longest metrics' name in pixel based on the label's font style and then add 45 for the width of the checkbox
            // next to the label and 20 for the colored square and 10 for +- padding
            const longestMetricNameInPixel = getLongestMetricNameInPixel(
                props.metrics.numericMetrics, props.metrics.tagMetrics,
                undefined, undefined, fontStyle,
            ) + 75;

            // Make sure the width is at least 100
            setMetricLabelWidthInPixel(Math.max(100, longestMetricNameInPixel));
        }
    }, [classes.metricLabel, props.metrics.numericMetrics, props.metrics.tagMetrics]);



    return (
        <SlideOutComp
            compContent={(
                <Paper className={clsx(panelClasses.root, classes.root, 'transparent', 'editing')}>
                    <div className={clsx(panelClasses.panelContent, classes.panelContent)}>
                        {((props.metrics.numericMetrics.length > 0 || props.metrics.tagMetrics.length > 0)
                            && (props.allowUnselectNumericMetrics !== false || props.allowUnselectTagMetrics !== false))
                            ? (
                                <div className={classes.selectMetricsSection}>
                                    <div className={classes.message}>
                                        {capitalizeFirst(trans('panel.multi_data_sources.select_metrics.label'))}
                                    </div>

                                    <div className={classes.metricsActions}>
                                        <SearchBox
                                            className={classes.searchBox}
                                            initialValue={metricSearchText ?? ''}
                                            onChange={setMetricSearchText}
                                            placeholder={capitalizeAll(trans('label.search'))}
                                        />
                                        <Button
                                            className={classes.selectAllButton}
                                            variant="contained"
                                            color="primary"
                                            size="small"
                                            onClick={() => {
                                                toggleAllMetrics(true);
                                            }}
                                        >
                                            {capitalizeFirst(trans('label.select_all'))}
                                        </Button>
                                        <Button
                                            className={classes.selectAllButton}
                                            variant="contained"
                                            color="primary"
                                            size="small"
                                            onClick={() => {
                                                toggleAllMetrics(false);
                                            }}
                                        >
                                            {capitalizeFirst(trans('label.unselect_all'))}
                                        </Button>
                                    </div>

                                    <div className={classes.metricsContainerWrapper}>
                                        <div className={classes.metricsContainer}>
                                            {props.allowUnselectNumericMetrics !== false && (
                                                <div className={clsx(classes.metricsGroup)}>
                                                    {filteredNumericMetrics.map((metric) => {
                                                        return (
                                                            <FormControlLabel
                                                                key={metric.id}
                                                                className={classes.metric}
                                                                control={(
                                                                    <Checkbox
                                                                        checked={selectedMetricIdsMap.numericMetrics[metric.id] ?? false}
                                                                        color="primary"
                                                                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                                                            onToggleOneMetric(metric.id, true, event.target.checked);
                                                                        }}
                                                                    />
                                                                )}
                                                                label={(
                                                                    <div
                                                                        className={classes.metricLabel}
                                                                        style={{ color: metric.color }}
                                                                    >
                                                                        <span className="label">
                                                                            {metric.displayName ?? metric.name ?? metric.id}
                                                                        </span>
                                                                    </div>
                                                                )}
                                                                labelPlacement="end"
                                                                style={{
                                                                    width: `${metricLabelWidthInPixel}px`,
                                                                }}
                                                            />
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                        {/* Add a divider between numeric metrics and tag metrics if there are options for tag metrics */}
                                        {props.allowUnselectTagMetrics && filteredTagMetrics.length > 0 && (
                                            <hr className={classes.metricsDivider} />
                                        )}
                                        <div className={classes.metricsContainer}>
                                            {props.allowUnselectTagMetrics !== false && (
                                                <div className={clsx(classes.metricsGroup)}>
                                                    {filteredTagMetrics.map((metric) => {
                                                        return (
                                                            <FormControlLabel
                                                                key={metric.id}
                                                                className={classes.metric}
                                                                control={(
                                                                    <Checkbox
                                                                        checked={selectedMetricIdsMap.tagMetrics[metric.id] ?? false}
                                                                        color="primary"
                                                                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                                                            onToggleOneMetric(metric.id, false, event.target.checked);
                                                                        }}
                                                                    />
                                                                )}
                                                                label={(
                                                                    <div
                                                                        className={classes.metricLabel}
                                                                        style={{ color: metric.color }}
                                                                    >
                                                                        <span className="label">
                                                                            {metric.displayName ?? metric.name ?? metric.id}
                                                                        </span>
                                                                    </div>
                                                                )}
                                                                labelPlacement="end"
                                                                style={{
                                                                    width: `${metricLabelWidthInPixel}px`,
                                                                }}
                                                            />
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <GenericMessageBox
                                    title={trans('generic.no_data.title', {
                                        term: capitalizeAll(trans('label.time_series_data', { count: 2 })),
                                    })}
                                />
                            )}
                        <div className={clsx(formClasses.formActionContainer)}>
                            <div />
                            <div>
                                <Button
                                    className={clsx(formClasses.formActionButton)}
                                    size="small"
                                    onClick={() => {
                                        hidePreviewPanel();
                                    }}
                                >
                                    {trans('label.cancel')}
                                </Button>
                                <Button
                                    className={clsx(formClasses.formActionButton)}
                                    variant="contained"
                                    color="primary"
                                    type="submit"
                                    size="small"
                                    onClick={onDoneUpdatingSettings}
                                >
                                    {trans('label.done')}
                                </Button>
                            </div>
                        </div>
                    </div>
                </Paper>
            )}
            closable={false}
            contentContainerProps={{
                className: 'slide-out-comp-container',
            }}
        />
    );
};
